import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useHiddenPaymentMethodsStore } from './HiddenPaymentMethodStore';
import type { PaymentMethodDto } from '../../generated-api';
import { useMemo } from 'react';

interface PaymentMethodActions {
  populateStore: (paymentMethods: PaymentMethodDto[], deviceId: string) => void;
}

interface PaymentMethodsStoreState {
  paymentMethods: PaymentMethodDto[];
  directSales: PaymentMethodDto[];
  openBill: PaymentMethodDto[];
  hiddenSnapshot: Set<string>;
  deviceId: string | undefined;
  deviceIdPaymentMethod: PaymentMethodDto | undefined;
  actions: PaymentMethodActions;
}

function filterPaymentMethods(paymentMethods: PaymentMethodDto[], hiddenIds: Set<string>) {
  const directSales = [];
  const openBill = [];

  for (const paymentMethod of paymentMethods) {
    if (paymentMethod.isDirectSalesMethod && !hiddenIds.has(paymentMethod.id)) {
      directSales.push(paymentMethod);
    }
    if (paymentMethod.isOpenBillMethod && !hiddenIds.has(paymentMethod.id)) {
      openBill.push(paymentMethod);
    }
  }

  return { directSales, openBill };
}

export const usePaymentMethodsStore = create<PaymentMethodsStoreState>()(
  subscribeWithSelector((set, get, api) => {
    useHiddenPaymentMethodsStore.subscribe((state) => {
      const { directSales, openBill } = filterPaymentMethods(get().paymentMethods, state.hiddenIds);

      set({
        directSales,
        hiddenSnapshot: state.hiddenIds,
        openBill,
      });
    });

    api.subscribe(
      (state) => state?.paymentMethods,
      (paymentMethods) => {
        const { directSales, openBill } = filterPaymentMethods(paymentMethods, get().hiddenSnapshot);

        set({
          directSales,
          openBill,
        });
      }
    );

    api.subscribe(
      (state) => state?.deviceId,
      (deviceId) => {
        if (deviceId === undefined) {
          set({ deviceIdPaymentMethod: undefined });
          return;
        }

        for (const paymentMethod of get().paymentMethods) {
          if (paymentMethod.id === deviceId) {
            set({ deviceIdPaymentMethod: paymentMethod });
            return;
          }
        }

        set({ deviceIdPaymentMethod: undefined });
      }
    );

    const initialHidden = useHiddenPaymentMethodsStore.getState().hiddenIds;

    return {
      actions: {
        populateStore: (paymentMethods, deviceId) => {
          set(() => {
            const merged: PaymentMethodDto[] = [];

            for (const pm of paymentMethods) {
              merged.push({ ...pm });
            }

            return { paymentMethods: merged, deviceId };
          });
        },
      },
      deviceId: undefined,
      deviceIdPaymentMethod: undefined,
      directSales: [],
      hiddenSnapshot: initialHidden,
      openBill: [],
      paymentMethods: [],
    };
  })
);
export function useDirectSalesPaymentMethods() {
  const devicePM = usePaymentMethodsStore((s) => s.deviceIdPaymentMethod);
  const all = usePaymentMethodsStore((s) => s.directSales);

  return useMemo(() => {
    const integrated = all.filter((s) => s.provider !== 'UNINTEGRATED');
    const unintegrated = all.filter((s) => s.provider === 'UNINTEGRATED');
    return devicePM ? [devicePM, ...unintegrated] : [...integrated, ...unintegrated];
  }, [devicePM, all]);
}

export function useOpenBillPaymentMethods() {
  const devicePM = usePaymentMethodsStore((s) => s.deviceIdPaymentMethod);
  const all = usePaymentMethodsStore((s) => s.openBill);

  return useMemo(() => {
    const integrated = all.filter((s) => s.provider !== 'UNINTEGRATED');
    const unintegrated = all.filter((s) => s.provider === 'UNINTEGRATED');
    return devicePM ? [devicePM, ...unintegrated] : [...integrated, ...unintegrated];
  }, [devicePM, all]);
}
export const usePaymentMethodActions = () => usePaymentMethodsStore((state) => state.actions);

const EMPTY_PAYMENT_METHODS: PaymentMethodDto[] = [];
export const useAvailablePaymentMethods = () =>
  usePaymentMethodsStore((state) => state.paymentMethods ?? EMPTY_PAYMENT_METHODS);

export function getPaymentMethodById(id: string): PaymentMethodDto | undefined {
  const methods = usePaymentMethodsStore.getState().paymentMethods;
  for (const method of methods) {
    if (method.id === id) {
      return method;
    }
  }
  return undefined;
}
